/**
 * B 表 · 对话主干 —— 契约 §5。形状以 `cione-agent-task` 的 DTO 为准。
 *
 * B1 run / B2 stream / B4 cancel 都已建成。
 * B3 `/api/ws/**` 目前只是回显验证，第一版不用，故本文件不映射。
 */
import { z } from 'zod'

/** `streamChannel` 的前缀，形如 `agent/9f2c...`。 */
export const STREAM_CHANNEL_PREFIX = 'agent/'

export const runRequestSchema = z.looseObject({
  conversationId: z.string().min(1),
  userInput: z.string(),
})
export type RunRequest = z.infer<typeof runRequestSchema>

/**
 * ⚠ 本响应 2026-09-01 从裸响应改成了套 `Result` 壳(契约 §5.1 的破坏性变更)。
 *   改在这个时点正是因为前端页面尚未开工 —— 这是最后一次免费的机会。
 */
export const runResponseSchema = z.looseObject({
  accepted: z.boolean(),
  streamChannel: z.string().min(1),
})
export type RunResponse = z.infer<typeof runResponseSchema>

/**
 * B4 取消。两个字段**给一个**即可。
 *
 * 推荐传 `streamChannel`：把 run 返回的原样回传，少一条口头约定。
 * ⚠ 契约驳回了「按 conversationId 取消」—— 全链路没有任何地方存着
 *   conversationId → taskId 的映射(taskId 是现生的随机 UUID，没有落库)。
 */
export const cancelRequestSchema = z
  .looseObject({
    taskId: z.string().min(1).optional(),
    streamChannel: z.string().min(1).optional(),
  })
  .refine((v) => Boolean(v.taskId) || Boolean(v.streamChannel), {
    message: 'taskId 与 streamChannel 必须给一个',
  })
export type CancelRequest = z.infer<typeof cancelRequestSchema>

/**
 * ⚠⚠ `accepted=true` **不等于「已经停了」**。
 *
 * 本接口发送的是取消**意图**，真正的中断由 Python 在下一个 `await` 点执行。
 * 界面**必须以流上收到 `done` 为准**再切「已停止」—— 按 `accepted` 就切，
 * 会看到「已停止」之后气泡还在长几十毫秒，反而像 bug。
 *
 * 幂等：重复取消同一个任务不报错，回 `alreadyTerminal=true`。
 */
export const cancelResponseSchema = z.looseObject({
  accepted: z.boolean(),
  alreadyTerminal: z.boolean(),
})
export type CancelResponse = z.infer<typeof cancelResponseSchema>
